import React from 'react';

import { formatCurrency, formatCurrencyNoDecimal, formatPercent } from './loan-details-template';
import { EXIT_STRATEGIES } from './PickList';

// TODO-Sreedevi-P4: This is not the right location for this component.
interface LoanDetailsComponentProps {
  loanData: any;
  showAdditionalDetails?: boolean;
  showBorrowerQualifications?: boolean;
  showOnlyAvailableValues?: boolean;
}

/**
 * React component for displaying loan details
 * This is used by the LoanDetailsForOfficerScreen component
 */
export const LoanDetailsComponent: React.FC<LoanDetailsComponentProps> = ({
  loanData,
  showAdditionalDetails = true,
  showBorrowerQualifications = true,
  showOnlyAvailableValues = false,
}) => {
  // Ensure loanData exists to prevent hydration errors
  if (!loanData) {
    return null;
  }
  
  const loanDetails = loanData.loanDetails || {};
  const loanInfo = loanData.loanInfo || {};
  const propertyType = loanData.propertyType || {};
  const loanPurpose = loanData.loanPurpose || {};
  const exitStrategy = loanData.exitStrategy || {};
  const ficoScore = loanData.ficoScore || {};
  const experienceLevel = loanData.experienceLevel || {};

  const hasValue = (value: any): boolean => value !== undefined && value !== null && value !== '';
  const showRow = (value: any): boolean => !showOnlyAvailableValues || hasValue(value);
  const getTextValue = (value: any): string => String(value ?? '');
  const getCurrencyValue = (value: any): string => formatCurrency(value);
  const getPercentValue = (value: any): string => formatPercent(value);
  const hasAdditionalDetails = [
    loanDetails?.additionalDetails?.loanTerm,
    loanDetails?.additionalDetails?.amortization,
    loanPurpose?.display,
    exitStrategy?.display,
    loanDetails?.additionalDetails?.lien,
    propertyType?.display,
    loanInfo?.asIsValue || loanInfo?.asIsPropertyValue || loanInfo?.propertyValue || loanInfo?.currentValue || loanInfo?.purchasePrice,
    exitStrategy?.key === EXIT_STRATEGIES.BRIDGE.key ? loanInfo?.asIsValue : loanInfo?.afterRepairValue,
    loanDetails?.additionalDetails?.loanToCost,
    loanDetails?.totalLoanAmount?.rehabHoldback,
    loanDetails?.additionalDetails?.prepaymentPenalty,
    loanDetails?.additionalDetails?.exitFee?.amount,
  ].some(hasValue);
  const hasBorrowerQualificationDetails = [
    loanDetails?.qualificationStatus,
    ficoScore?.display,
    experienceLevel?.display,
    loanDetails?.minimumAvailableLiquidity,
  ].some(hasValue);
  // Debugging: Examine what's returned from the component
  // TODO-Sreedevi-P4: Fix in-consistent checks and formatting of fields. loanToCost formatting is off. 
  // TODO-Sreedevi-P4: Make all the {/* Spacer row */} readable without need for comments
  const content = (
    <>
      {/* Spacer row */}
      <tr><td colSpan={3} style={{ height: '5px' }}></td></tr>
      
      {showRow(loanDetails?.totalLoanAmount?.total) && (
        <tr>
          <td className="font-medium py-1">Total Loan Amount</td>
          <td className="value-cell text-right py-1">{getCurrencyValue(loanDetails?.totalLoanAmount?.total)}</td>
          <td className="notes-cell"></td>
        </tr>
      )}
      
      {showRow(loanDetails?.totalLoanAmount?.acquisitionLoan) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">{loanPurpose?.display || 'Loan'} Amount</td>
          <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanDetails?.totalLoanAmount?.acquisitionLoan)}</td>
          <td className="notes-cell"></td>
        </tr>
      )}
      
      {showRow(loanDetails?.totalLoanAmount?.rehabHoldback) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Rehab Holdback</td>
          <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanDetails?.totalLoanAmount?.rehabHoldback)}</td>
          <td className="notes-cell"></td>
        </tr>
      )}
      
      {/* Spacer row */}
      <tr><td colSpan={3} style={{ height: '5px' }}></td></tr>
      
      {showRow(loanDetails?.monthlyPayment?.initial) && (
        <tr>
          <td className="font-medium py-1">Monthly Payment (Initial)</td>
          <td className="value-cell text-right py-1">{getCurrencyValue(loanDetails?.monthlyPayment?.initial)}</td>
          <td className="notes-cell"></td>
        </tr>
      )}
      
      {showRow(loanDetails?.monthlyPayment?.fullyDrawn) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Monthly Payment (Fully Drawn)</td>
          <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanDetails?.monthlyPayment?.fullyDrawn)}</td>
          <td className="notes-cell"></td>
        </tr>
      )}
      
      {/* Spacer row */}
      <tr><td colSpan={3} style={{ height: '5px' }}></td></tr>
      
      {showRow(loanDetails?.interestRate) && (
        <tr>
          <td className="font-medium py-1">Interest Rate</td>
          <td className="value-cell text-right py-1">{getPercentValue(loanDetails?.interestRate)}</td>
          <td className="notes-cell"></td>
        </tr>
      )}
      
      {/* Spacer row */}
      <tr><td colSpan={3} style={{ height: '5px' }}></td></tr>
      
      {showRow(loanDetails?.estNetCashOutAtClosing) && (
        <tr>
          <td className="font-medium py-1">{loanDetails?.isNegativeCashFlow ? 'Est. Net Cash Out at Closing' : 'Est. Cash Required at Closing'}</td>
          <td className="value-cell text-right py-1">{getCurrencyValue(loanDetails?.estNetCashOutAtClosing)}</td>
          <td className="notes-cell text-left text-gray-500 text-xs py-1 pl-2">
            {loanDetails?.cashRequired?.breakdown?.purchasePriceMessage && (
              <span>{loanDetails.cashRequired.breakdown.purchasePriceMessage}</span>
            )}
          </td>
        </tr>
      )}

      {(loanPurpose.display === 'Purchase' || (!showOnlyAvailableValues && loanPurpose.display !== 'Purchase')) && showRow(loanInfo?.purchasePrice) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Purchase Price</td>
          <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanInfo?.purchasePrice)}</td>
          <td className="notes-cell"></td>
        </tr>
      )}

      {(loanPurpose.display !== 'Purchase') && showRow(loanInfo?.currentLoanBalance) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Estimated Loan Payoff(s)</td>
          <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanInfo?.currentLoanBalance)}</td>
          <td className="notes-cell"></td>
        </tr>
      )}
      
      {/* Fees Section - now under Est. Cash Required at Closing */}
      {showRow(loanDetails?.cashRequired?.breakdown?.originationFee?.amount) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Origination Fee</td>
          <td className="value-cell text-right text-gray-600 py-1">{loanDetails?.cashRequired?.breakdown?.originationFee ? getCurrencyValue(loanDetails.cashRequired.breakdown.originationFee.amount) : '$0'}</td>
          <td className="notes-cell text-left text-gray-500 text-xs py-1 pl-2">
            {loanDetails?.cashRequired?.breakdown?.originationFee?.note && (
              <span>{loanDetails.cashRequired.breakdown.originationFee.note}</span>
            )}
          </td>
        </tr>
      )}
      
        {showRow(loanDetails?.cashRequired?.breakdown?.brokerFee?.amount) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Broker Fee</td>
          <td className="value-cell text-right text-gray-600 py-1">{formatCurrency(loanDetails?.cashRequired?.breakdown?.brokerFee?.amount || 0)}</td>
          <td className="notes-cell text-left text-gray-500 text-xs py-1 pl-2">
            {loanDetails?.cashRequired?.breakdown?.brokerFee?.note && loanDetails?.cashRequired?.breakdown?.brokerFee?.amount !== undefined && (
              <span>{loanDetails.cashRequired.breakdown.brokerFee.note}</span>
            )}
          </td>
        </tr>
        )}
      
      {showRow(loanDetails?.cashRequired?.breakdown?.prepaidInterestReserve?.amount) && (
      <tr>
        <td className="indent-1 pl-5 text-gray-600 py-1">Prepaid Interest Reserve</td>
        <td className="value-cell text-right text-gray-600 py-1">{formatCurrencyNoDecimal(loanDetails?.cashRequired?.breakdown?.prepaidInterestReserve?.amount || 0)}</td>
        <td className="notes-cell text-left text-gray-500 text-xs py-1 pl-2">
            {loanDetails?.cashRequired?.breakdown?.prepaidInterestReserve?.note && (
              <span>{loanDetails.cashRequired.breakdown.prepaidInterestReserve.note}</span>
            )}
          </td>
      </tr>
      )}
      
        {showRow(loanDetails?.cashRequired?.breakdown?.perDiemInterest?.amount) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Per-Diem Interest</td>
          <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanDetails?.cashRequired?.breakdown?.perDiemInterest?.amount)}</td>
          <td className="notes-cell text-left text-gray-500 text-xs py-1 pl-2">
            {loanDetails?.cashRequired?.breakdown?.perDiemInterest?.note && (
              <span>{loanDetails.cashRequired.breakdown.perDiemInterest.note}</span>
            )}
          </td>
        </tr>
        )}
      
        {showRow(loanDetails?.cashRequired?.breakdown?.processingFees) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Loan Processing & Servicing Fees</td>
          <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanDetails?.cashRequired?.breakdown?.processingFees)}</td>
          <td className="notes-cell"></td>
        </tr>
        )}
      
        {showRow(loanDetails?.cashRequired?.breakdown?.legalFees) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Est. Legal Costs</td>
          <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanDetails?.cashRequired?.breakdown?.legalFees)}</td>
          <td className="notes-cell"></td>
        </tr>
        )}
      
      <tr>
        <td className="indent-1 pl-5 text-gray-600 py-1">Third Party Costs<sup className="text-xs align-super">*</sup></td>
        <td className="value-cell text-right text-gray-600 py-1">Contact closing agent</td>
        <td className="notes-cell"></td>
      </tr>
      
      {showAdditionalDetails && (!showOnlyAvailableValues || hasAdditionalDetails) && (
	    <>
	      {/* Spacer row */}
	      <tr><td colSpan={3} style={{ height: '5px' }}></td></tr>
	      
	      <tr>
	        <td className="font-medium py-1">Additional Details</td>
	        <td className="value-cell"></td>
	        <td className="notes-cell"></td>
	      </tr>
	      
	        {showRow(loanDetails?.additionalDetails?.loanTerm) && (
	        <tr>
	          <td className="indent-1 pl-5 text-gray-600 py-1">Loan Term</td>
	          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(loanDetails?.additionalDetails?.loanTerm)}</td>
	          <td className="notes-cell"></td>
	        </tr>
          )}

	        {showRow(loanDetails?.additionalDetails?.amortization) && (
	        <tr>
	          <td className="indent-1 pl-5 text-gray-600 py-1">Amortization</td>
	          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(loanDetails?.additionalDetails?.amortization)}</td>
	          <td className="notes-cell"></td>
	        </tr>
          )}

	      <tr>
	        <td className="indent-1 pl-5 text-gray-600 py-1">Preferred Signing Date</td>
	        <td className="value-cell text-right text-gray-600 py-1">As soon as possible</td>
	        <td className="notes-cell"></td>
	      </tr>
	      
	        {showRow(loanPurpose?.display) && (
	        <tr>
	          <td className="indent-1 pl-5 text-gray-600 py-1">Loan Purpose</td>
	          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(loanPurpose?.display)}</td>
	          <td className="notes-cell"></td>
	        </tr>
          )}
	      
	        {showRow(exitStrategy?.display) && (
	        <tr>
	          <td className="indent-1 pl-5 text-gray-600 py-1">Exit Strategy</td>
	          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(exitStrategy?.display)}</td>
	          <td className="notes-cell"></td>
	        </tr>
          )}
	      
	        {showRow(loanDetails?.additionalDetails?.lien) && (
	        <tr>
	          <td className="indent-1 pl-5 text-gray-600 py-1">Lien</td>
	          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(loanDetails?.additionalDetails?.lien)}</td>
	          <td className="notes-cell"></td>
	        </tr>
          )}
	      
	        {showRow(propertyType?.display) && (
	        <tr>
	          <td className="indent-1 pl-5 text-gray-600 py-1">Property Type</td>
	          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(propertyType?.display)}</td>
	          <td className="notes-cell"></td>
	        </tr>
          )}
	      
	      <tr>
	        <td className="indent-1 pl-5 text-gray-600 py-1">Occupancy</td>
	        <td className="value-cell text-right text-gray-600 py-1">Non-Owner Occupied</td>
	        <td className="notes-cell"></td>
	      </tr>
	    </>
	  )}

      {/* Always show As-Is Value with fallback */}
      {showRow(loanInfo?.asIsValue || loanInfo?.asIsPropertyValue || loanInfo?.propertyValue || loanInfo?.currentValue || (loanInfo?.purchasePrice ? loanInfo.purchasePrice : null)) && (
      <tr>
        <td className="indent-1 pl-5 text-gray-600 py-1">As-Is Value</td>
        <td className="value-cell text-right text-gray-600 py-1">
          {getCurrencyValue(loanInfo?.asIsValue || loanInfo?.asIsPropertyValue || loanInfo?.propertyValue || loanInfo?.currentValue || 
          (loanInfo?.purchasePrice ? loanInfo.purchasePrice : null)) || 'Not Available'}
        </td>
        <td className="notes-cell"></td>
      </tr>
      )}
      
      {/* Always show Estimated ARV with fallback */}
      {showRow(exitStrategy?.key === EXIT_STRATEGIES.BRIDGE.key ? loanInfo?.asIsValue : loanInfo?.afterRepairValue) && (
      <tr>
        <td className="indent-1 pl-5 text-gray-600 py-1">Estimated ARV</td>
        <td className="value-cell text-right text-gray-600 py-1">
          {exitStrategy?.key === EXIT_STRATEGIES.BRIDGE.key ? getCurrencyValue(loanInfo?.asIsValue) || 'Not Available' : getCurrencyValue(loanInfo?.afterRepairValue) || 'Not Available'}
        </td>
        <td className="notes-cell"></td>
      </tr>
      )}
      
      {showRow(loanDetails?.additionalDetails?.loanToCost) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Loan-to-Cost</td>
          <td className="value-cell text-right text-gray-600 py-1">
            {(() => {
              const ltcValue = loanDetails?.additionalDetails?.loanToCost;
              if (ltcValue === Infinity || ltcValue === -Infinity || !isFinite(ltcValue)) {
                return '0.00';
              }
              if (typeof ltcValue === 'number' || !isNaN(parseFloat(ltcValue))) {
                return parseFloat(ltcValue).toFixed(2);
              }
              return '0.00';
            })()}%
          </td>
          <td className="notes-cell"></td>
        </tr>
      )}
    
        {showRow(loanDetails?.totalLoanAmount?.rehabHoldback) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Holdback Amount</td>
          <td className="value-cell text-right text-gray-600 py-1">{formatCurrency(loanDetails?.totalLoanAmount?.rehabHoldback)}</td>
          <td className="notes-cell"></td>
        </tr>
        )}
      
        {loanDetails?.isEquityRequired && showRow(loanDetails?.requiredEquityAmount) && ( 
          <tr>
            <td className="indent-1 pl-5 text-gray-600 py-1">Remaining Equity Contribution<sup className="text-xs align-super">#</sup></td>
            <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanDetails?.requiredEquityAmount)}</td>
            <td className="notes-cell"></td>
          </tr>
        )}

        {showRow(loanDetails?.additionalDetails?.prepaymentPenalty) && (
        <tr>
          <td className="indent-1 pl-5 text-gray-600 py-1">Prepayment Penalty</td>
          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(loanDetails?.additionalDetails?.prepaymentPenalty)}</td>
          <td className="notes-cell"></td>
        </tr>
        )}
      
      {showRow(loanDetails?.additionalDetails?.exitFee?.amount) && (
      <tr>
        <td className="indent-1 pl-5 text-gray-600 py-1">Exit Fee</td>
        <td className="value-cell text-right text-gray-600 py-1">{loanDetails?.additionalDetails?.exitFee ? getCurrencyValue(loanDetails.additionalDetails.exitFee.amount) : '$0'}</td>
        <td className="notes-cell text-left text-gray-500 text-xs py-1 pl-2">
          {loanDetails?.additionalDetails?.exitFee?.note && (
            <span>{loanDetails.additionalDetails.exitFee.note}</span>
          )}
        </td>
      </tr>
      )}
      
      {/* Spacer row */}
      <tr><td colSpan={3} style={{ height: '5px' }}></td></tr>
      
      {showBorrowerQualifications && (!showOnlyAvailableValues || hasBorrowerQualificationDetails) && (
	    <>
	      <tr><td colSpan={3} style={{ height: '5px' }}></td></tr>
	      
	      {showRow(loanDetails?.qualificationStatus) && (
	      <tr>
	        <td className="font-medium py-1">Borrower Qualifications</td>
	        <td className="value-cell text-right py-1">{loanDetails?.qualificationStatus || ''}</td>
	        <td className="notes-cell"></td>
	      </tr>
          )}
	      
	        {showRow(ficoScore?.display) && (
	        <tr>
	          <td className="indent-1 pl-5 text-gray-600 py-1">FICO Score</td>
	          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(ficoScore?.display)}</td>
	          <td className="notes-cell"></td>
	        </tr>
          )}

	        {showRow(experienceLevel?.display) && (
	        <tr>
	          <td className="indent-1 pl-5 text-gray-600 py-1">Real Estate Experience</td>
	          <td className="value-cell text-right text-gray-600 py-1">{getTextValue(experienceLevel?.display)}</td>
	          <td className="notes-cell"></td>
	        </tr>
          )}
	      
	      {showRow(loanDetails?.minimumAvailableLiquidity) && (
	      <tr>
	        <td className="indent-1 pl-5 text-gray-600 py-1">Minimum Available Liquidity</td>
	        <td className="value-cell text-right text-gray-600 py-1">{getCurrencyValue(loanDetails?.minimumAvailableLiquidity)}</td>
	        <td className="notes-cell"></td>
	      </tr>
          )}
	    </>
	  )}
    </>
  );
  
  return content;
};

/**
 * React component for displaying loan details footer notes
 */
export const LoanDetailsFooterComponent: React.FC<LoanDetailsComponentProps> = ({ loanData }) => {
  // Ensure loanData exists to prevent hydration errors
  if (!loanData) {
    return null;
  }
  
  const loanDetails = loanData.loanDetails || {};
  const hasEquityRequirement = Boolean(loanDetails?.requiredEquityAmount);
  
  // Completely rewritten component to eliminate any potential stray text nodes
  return (
    <div className="mt-4 text-xs text-gray-500">
      <div>
        <p><sup>*</sup> Third party settlement costs - contact closing agent for details</p>
      </div>
      {hasEquityRequirement && (
        <div>
          <p><sup>#</sup> Prior to disbursement of any construction funds, Stormfield will verify that borrower has met their Remaining Borrower Equity Contribution</p>
        </div>
      )}
    </div>
  );
};
